import React, { useState, useEffect } from 'react';
import {
  X,
  ChevronLeft,
  Loader2,
  Camera,
  Plus,
  BookOpen,
  AlertCircle,
  RefreshCw,
} from 'lucide-react';
import { Cigar, CIGARS } from '../data/cigars';

// Helper to get cigar by ID
const getCigarById = (id: string): Cigar | undefined => {
  return CIGARS.find(c => c.id === id);
};

interface IdentifyResult {
  identified: boolean;
  cigarId?: string;
  brand?: string;
  line?: string;
  confidence: number;
  method: 'workers_ai' | 'claude_vision' | 'not_found';
  photoUrl?: string;
  rawResponse?: string;
}

interface CigarPrice {
  cigarId: string;
  brand: string;
  line: string;
  singlePriceLow?: number;
  singlePriceHigh?: number;
  boxPriceLow?: number;
  boxPriceHigh?: number;
  boxCount?: number;
}

interface SimilarCigar extends CigarPrice {
  priceDelta: number;
}

interface CigarIdentifyResultProps {
  isOpen: boolean;
  onClose: () => void;
  result: IdentifyResult | null;
  isLoading: boolean;
  onAddToHumidor?: (cigar: Cigar) => void;
  onLogSmoke?: (cigar: Cigar) => void;
  onRetry?: () => void;
}

export const CigarIdentifyResult: React.FC<CigarIdentifyResultProps> = ({
  isOpen,
  onClose,
  result,
  isLoading,
  onAddToHumidor,
  onLogSmoke,
  onRetry,
}) => {
  const [price, setPrice] = useState<CigarPrice | null>(null);
  const [similar, setSimilar] = useState<SimilarCigar[]>([]);
  const [loadingPrice, setLoadingPrice] = useState(false);
  const [cigar, setCigar] = useState<Cigar | null>(null);

  // Fetch price and similar cigars when result changes
  useEffect(() => {
    if (result?.identified && result.cigarId) {
      // Get cigar details from local database
      const cigarData = getCigarById(result.cigarId);
      setCigar(cigarData || null);

      // Fetch price from API
      setLoadingPrice(true);
      Promise.all([
        fetch(`/api/prices/${result.cigarId}`).then(r => r.json()) as Promise<{ data?: { found: boolean; price?: CigarPrice } }>,
        fetch(`/api/similar/${result.cigarId}`).then(r => r.json()) as Promise<{ data?: { similar: SimilarCigar[] } }>,
      ])
        .then(([priceRes, similarRes]) => {
          if (priceRes.data?.found && priceRes.data.price) {
            setPrice(priceRes.data.price);
          }
          if (similarRes.data?.similar) {
            setSimilar(similarRes.data.similar);
          }
        })
        .catch(console.error)
        .finally(() => setLoadingPrice(false));
    } else {
      setPrice(null);
      setSimilar([]);
      setCigar(null);
    }
  }, [result]);

  if (!isOpen) return null;

  const strengthColors: Record<string, string> = {
    'mild': 'bg-green-500',
    'mild-medium': 'bg-lime-500',
    'medium': 'bg-amber-500',
    'medium-full': 'bg-orange-500',
    'full': 'bg-red-500',
  };

  const renderStrengthDots = (strength?: string) => {
    const levels = ['mild', 'mild-medium', 'medium', 'medium-full', 'full'];
    const currentIndex = levels.indexOf(strength || 'medium');
    return (
      <div className="flex gap-1">
        {levels.map((_, i) => (
          <div
            key={i}
            className={`w-2 h-2 rounded-full ${
              i <= currentIndex ? strengthColors[strength || 'medium'] : 'bg-white/20'
            }`}
          />
        ))}
      </div>
    );
  };

  const formatPrice = (low?: number, high?: number) => {
    if (!low && !high) return 'N/A';
    if (low === high || !high) return `$${low}`;
    return `$${low}-${high}`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-md max-h-[90vh] bg-stone-900 rounded-t-2xl sm:rounded-2xl border border-white/10 shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <button
            onClick={onClose}
            className="p-2 -ml-2 rounded-full hover:bg-white/10 text-white/60 hover:text-white transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h2 className="text-lg font-semibold text-white">
            {isLoading ? 'Identifying...' : result?.identified ? 'Identified!' : 'Results'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 -mr-2 rounded-full hover:bg-white/10 text-white/60 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Loading State */}
          {isLoading && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-20 h-20 rounded-full bg-amber-500/20 flex items-center justify-center mb-4">
                <Loader2 className="w-10 h-10 text-amber-500 animate-spin" />
              </div>
              <p className="text-white font-medium">Analyzing cigar band...</p>
              <p className="text-white/50 text-sm mt-1">This may take a moment</p>
            </div>
          )}

          {/* Success State */}
          {!isLoading && result?.identified && (
            <>
              {/* Hero Card */}
              <div className="bg-white/5 rounded-xl p-4 border border-white/10 mb-4">
                <div className="flex gap-4">
                  {/* Photo thumbnail */}
                  {result.photoUrl && (
                    <div className="w-20 h-20 rounded-lg overflow-hidden bg-stone-800 flex-shrink-0">
                      <img
                        src={result.photoUrl}
                        alt="Cigar band"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-bold text-lg">{result.brand}</h3>
                    <p className="text-amber-500">{result.line}</p>
                    <div className="flex items-center gap-2 mt-2">
                      {renderStrengthDots(cigar?.strength)}
                      <span className="text-white/50 text-sm capitalize">
                        {cigar?.strength?.replace('-', ' ')}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Price */}
                {loadingPrice ? (
                  <div className="mt-4 flex items-center gap-2 text-white/50">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm">Loading prices...</span>
                  </div>
                ) : price ? (
                  <div className="mt-4 flex items-center gap-4 text-white">
                    <div>
                      <span className="text-white/50 text-xs block">Single</span>
                      <span className="font-semibold">
                        {formatPrice(price.singlePriceLow, price.singlePriceHigh)}
                      </span>
                    </div>
                    <div className="w-px h-8 bg-white/20" />
                    <div>
                      <span className="text-white/50 text-xs block">
                        Box{price.boxCount ? `/${price.boxCount}` : ''}
                      </span>
                      <span className="font-semibold">
                        {formatPrice(price.boxPriceLow, price.boxPriceHigh)}
                      </span>
                    </div>
                  </div>
                ) : null}

                {/* Flavor tags */}
                {cigar?.primaryFlavors && (
                  <div className="flex flex-wrap gap-1.5 mt-4">
                    {cigar.primaryFlavors.slice(0, 4).map(flavor => (
                      <span
                        key={flavor}
                        className="px-2 py-0.5 rounded-full bg-white/10 text-white/70 text-xs"
                      >
                        {flavor}
                      </span>
                    ))}
                  </div>
                )}

                {/* Confidence indicator */}
                <div className="mt-4 pt-4 border-t border-white/10 flex items-center justify-between">
                  <span className="text-white/40 text-xs">
                    Confidence: {result.confidence}%
                  </span>
                  <span className="text-white/40 text-xs capitalize">
                    via {result.method.replace('_', ' ')}
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 mb-6">
                {onAddToHumidor && cigar && (
                  <button
                    onClick={() => onAddToHumidor(cigar)}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-amber-500 text-stone-900 rounded-xl font-medium hover:bg-amber-400 transition-colors"
                  >
                    <Plus className="w-5 h-5" />
                    Add to Humidor
                  </button>
                )}
                {onLogSmoke && cigar && (
                  <button
                    onClick={() => onLogSmoke(cigar)}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-white/10 text-white rounded-xl font-medium hover:bg-white/20 transition-colors"
                  >
                    <BookOpen className="w-5 h-5" />
                    Log Smoke
                  </button>
                )}
              </div>

              {/* Similar Cigars */}
              {similar.length > 0 && (
                <div>
                  <h4 className="text-white/50 text-xs uppercase tracking-wide mb-3">
                    Similar Cigars
                  </h4>
                  <div className="space-y-2">
                    {similar.map(s => (
                      <div
                        key={s.cigarId}
                        className="flex items-center justify-between p-3 bg-white/5 rounded-xl border border-white/10"
                      >
                        <div>
                          <p className="text-white font-medium">{s.brand}</p>
                          <p className="text-white/50 text-sm">{s.line}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-white font-medium">
                            {formatPrice(s.singlePriceLow, s.singlePriceHigh)}
                          </p>
                          {s.priceDelta !== 0 && (
                            <p className={`text-xs ${s.priceDelta < 0 ? 'text-green-400' : 'text-white/50'}`}>
                              {s.priceDelta < 0 ? `Save $${Math.abs(s.priceDelta).toFixed(0)}` : `+$${s.priceDelta.toFixed(0)}`}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Not Found State */}
          {!isLoading && result && !result.identified && (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="w-20 h-20 rounded-full bg-white/10 flex items-center justify-center mb-4">
                <AlertCircle className="w-10 h-10 text-white/40" />
              </div>
              <h3 className="text-white font-medium mb-2">Couldn't identify this cigar</h3>
              <p className="text-white/50 text-sm text-center mb-6 max-w-xs">
                Try taking a clearer photo of the band, or search manually in the Explorer.
              </p>

              {result.rawResponse && (
                <div className="w-full p-3 bg-white/5 rounded-lg mb-4">
                  <p className="text-white/40 text-xs mb-1">AI Response:</p>
                  <p className="text-white/70 text-sm">{result.rawResponse}</p>
                </div>
              )}

              <div className="flex gap-3">
                {onRetry && (
                  <button
                    onClick={onRetry}
                    className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-stone-900 rounded-lg font-medium hover:bg-amber-400 transition-colors"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Try Again
                  </button>
                )}
                <button
                  onClick={onClose}
                  className="flex items-center gap-2 px-4 py-2 bg-white/10 text-white rounded-lg font-medium hover:bg-white/20 transition-colors"
                >
                  <Camera className="w-4 h-4" />
                  Log Manually
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CigarIdentifyResult;
